import { useState, type CSSProperties, type MouseEvent } from "react";
import { motion } from "framer-motion";
import { GlassCard, ScreenShell, SimHeader } from "./shared";

export interface MCOption { letter: string; text: string; }

export interface MCData {
  decisionNumber: number;
  totalDecisions: number;
  type: string;
  competency: string;
  scenario: string;
  options: MCOption[];
}

export function AnswerMCScreen({
  data,
  initialSelected = null,
  xp = 0,
  timerDisplay,
  onSelect,
  onSubmit,
  scoring = false,
  error,
}: {
  data: MCData;
  initialSelected?: string | null;
  xp?: number;
  timerDisplay?: string;
  onSelect?: (letter: string) => void;
  onSubmit?: (letter: string) => void;
  scoring?: boolean;
  error?: string | null;
}) {
  const [selected, setSelected] = useState<string | null>(initialSelected);
  const progress = (data.decisionNumber / data.totalDecisions) * 100;

  return (
    <ScreenShell>
      <SimHeader decision={data.decisionNumber} xp={xp} timerDisplay={timerDisplay} />
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-8 px-4 pb-12 md:grid-cols-[38%_62%] md:px-8">
        {/* LEFT */}
        <div className="flex flex-col gap-3.5">
          <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/45">
            Decision {data.decisionNumber} of {data.totalDecisions}
          </div>
          <div className="h-[3px] w-full overflow-hidden rounded-full bg-white/5">
            <div className="h-full transition-all duration-700" style={{ width:`${progress}%`, background:"linear-gradient(90deg, #8B5CF6, #22D3EE)" }} />
          </div>
          <div className="flex flex-wrap gap-2">
            <Pill color="#22D3EE">{data.type}</Pill>
            <Pill color="#A78BFA">{data.competency}</Pill>
          </div>
          <GlassCard className="p-5" animatedBorder>
            <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.2em] text-white/40">The Situation</div>
            <div className="text-[14px] leading-[1.7]" style={{ color:"#CBD5E1" }}>{data.scenario}</div>
          </GlassCard>
          <div className="rounded-2xl border p-4 text-[12px] text-white/55"
            style={{ background:"rgba(139,92,246,0.08)", borderColor:"rgba(139,92,246,0.25)" }}>
            <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.2em]" style={{ color:"#A78BFA" }}>AI is evaluating</div>
            Your response is assessed for leadership quality and depth, not length alone.
          </div>
        </div>

        {/* RIGHT */}
        <div className="flex flex-col gap-3 md:pt-[3.75rem]">
          <div className="text-[13px] font-medium uppercase tracking-[0.2em] text-white/55">Choose your response</div>
          {data.options.map((o) => (
            <OptionCard key={o.letter} option={o} selected={selected === o.letter} disabled={scoring}
              onClick={() => { if (scoring) return; setSelected(o.letter); onSelect?.(o.letter); }} />
          ))}
          {selected && !scoring && (
            <button onClick={() => onSubmit?.(selected)}
              className="cta-gradient-warm relative mt-2 overflow-hidden rounded-2xl py-4 text-[15px] font-bold text-white">
              Submit Response →
            </button>
          )}
          {scoring && (
            <div className="mt-2 flex items-center justify-center gap-2 py-4 text-[14px] text-white/60">
              <Spinner /> Scoring your response…
            </div>
          )}
          {error && <p className="text-sm text-rose-300">{error}</p>}
          <div className="mt-6 flex flex-col gap-3">
            <div className="h-px w-full bg-white/5" />
            <div className="flex flex-wrap items-center justify-between gap-3 text-[11px] text-white/35">
              <span>⏱ Take your time</span>
              <span>🧠 No right answer is obvious</span>
              <span>🔒 Only you see your choices</span>
            </div>
          </div>
        </div>
      </div>
    </ScreenShell>
  );
}

function Pill({ children, color }: { children: React.ReactNode; color: string }) {
  return (
    <span className="rounded-full border px-3 py-1 text-[11px] font-medium"
      style={{ background:`${color}26`, borderColor:`${color}66`, color }}>
      {children}
    </span>
  );
}

function OptionCard({ option, selected, disabled, onClick }: {
  option: MCOption; selected: boolean; disabled?: boolean; onClick: () => void;
}) {
  const [ripples, setRipples] = useState<{ id: number; x: number; y: number }[]>([]);
  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    if (disabled) return;
    const r = e.currentTarget.getBoundingClientRect();
    const id = Date.now();
    setRipples(rs => [...rs, { id, x:e.clientX-r.left, y:e.clientY-r.top }]);
    setTimeout(() => setRipples(rs => rs.filter(x => x.id !== id)), 700);
    onClick();
  };
  return (
    <motion.button onClick={handleClick} disabled={disabled}
      animate={selected ? { scale:[1,1.04,1] } : { scale:1 }}
      transition={{ duration:0.35, ease:[0.16,1,0.3,1] }}
      onMouseMove={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        e.currentTarget.style.setProperty("--mx", `${e.clientX-r.left}px`);
        e.currentTarget.style.setProperty("--my", `${e.clientY-r.top}px`);
      }}
      className="group relative overflow-hidden rounded-2xl border p-4 text-left transition-colors disabled:opacity-50"
      style={{ background:selected?"rgba(139,92,246,0.15)":"rgba(255,255,255,0.04)", borderColor:selected?"rgba(139,92,246,0.55)":"rgba(255,255,255,0.08)" }}>
      <span className="pointer-events-none absolute inset-0 opacity-0 transition-opacity group-hover:opacity-100"
        style={{ background:"radial-gradient(280px circle at var(--mx) var(--my), rgba(139,92,246,0.15), transparent 70%)" } as CSSProperties} />
      {ripples.map(r => (
        <span key={r.id} className="pointer-events-none absolute h-6 w-6 rounded-full"
          style={{ left:r.x-12, top:r.y-12, background:"rgba(167,139,250,0.45)", animation:"ripple-out 0.7s ease-out forwards" }} />
      ))}
      <div className="relative flex gap-3">
        <motion.div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-[12px] font-extrabold"
          animate={{ background:selected?"#8B5CF6":"rgba(255,255,255,0.1)", color:selected?"#ffffff":"rgba(255,255,255,0.85)", rotateY:selected?[0,180,360]:0 }}
          transition={{ background:{ duration:0.25 }, rotateY:{ type:"spring", stiffness:260, damping:16 } }}>
          {option.letter}
        </motion.div>
        <div className="text-[14px] leading-[1.55]" style={{ color:"rgba(255,255,255,0.9)" }}>{option.text}</div>
      </div>
    </motion.button>
  );
}

function Spinner() {
  return <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />;
}
